'use client';

import { useBoolean } from '@insta-club/hooks';
import { Plan, Subscription } from '@insta-club/models';
import { CloseBtn, Dialog, GuideVideo, HelpButton } from '@insta-club/ui';
import { cn } from '@insta-club/utilities';
import Image from 'next/image';
import { useMemo } from 'react';
import BuyBottomSheet from './buy-bottom-sheet';
import PlanItem from './plan-item';

interface Props {
    item: Subscription;
    selectedPlan: Plan | null;
    onSelectPlan: (plan: Plan) => void;
}

// const containerVariants = cva('border rounded-2xl flex flex-col gap-6 p-4 overflow-hidden', {
//     variants: {
//         variant: {
//             default: 'border-primary-300 bg-primary-50',
//             gray: 'border-gray-300 bg-gray-50',
//             gold: 'border-yellow-300 bg-yellow-50'
//         }
//     },
//     defaultVariants: {
//         variant: 'default'
//     }
// });

function Facilities({ text }: { text: string }) {
    return (
        <div className='flex items-center gap-2'>
            <svg
                className='text-success-500'
                width='20'
                height='20'
                viewBox='0 0 30 30'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M26.9501 13.4252L25.2501 11.4502C24.9251 11.0752 24.6626 10.3752 24.6626 9.8752V7.7502C24.6626 6.4252 23.5751 5.3377 22.2501 5.3377H20.1251C19.6376 5.3377 18.9251 5.0752 18.5501 4.7502L16.5751 3.0502C15.7126 2.3127 14.3001 2.3127 13.4251 3.0502L11.4626 4.7627C11.0876 5.0752 10.3751 5.3377 9.8876 5.3377H7.7251C6.4001 5.3377 5.3126 6.4252 5.3126 7.7502V9.8877C5.3126 10.3752 5.0501 11.0752 4.7376 11.4502L3.0501 13.4377C2.3251 14.3002 2.3251 15.7002 3.0501 16.5627L4.7376 18.5502C5.0501 18.9252 5.3126 19.6252 5.3126 20.1127V22.2502C5.3126 23.5752 6.4001 24.6627 7.7251 24.6627H9.8876C10.3751 24.6627 11.0876 24.9252 11.4626 25.2502L13.4376 26.9502C14.3001 27.6877 15.7126 27.6877 16.5876 26.9502L18.5626 25.2502C18.9376 24.9252 19.6376 24.6627 20.1376 24.6627H22.2626C23.5876 24.6627 24.6751 23.5752 24.6751 22.2502V20.1252C24.6751 19.6377 24.9376 18.9252 25.2626 18.5502L26.9626 16.5752C27.6876 15.7127 27.6876 14.2877 26.9501 13.4252ZM20.2001 12.6377L14.1626 18.6752C13.9876 18.8502 13.7501 18.9502 13.5001 18.9502C13.2501 18.9502 13.0126 18.8502 12.8376 18.6752L9.8126 15.6502C9.4501 15.2877 9.4501 14.6877 9.8126 14.3252C10.1751 13.9627 10.7751 13.9627 11.1376 14.3252L13.5001 16.6877L18.8751 11.3127C19.2376 10.9502 19.8376 10.9502 20.2001 11.3127C20.5626 11.6752 20.5626 12.2752 20.2001 12.6377Z'
                    fill='currentColor'
                />
            </svg>
            <span className='text-sm font-medium text-gray-600'>{text}</span>
        </div>
    );
}

const SubscriptionItem: React.FC<Props> = ({ item, selectedPlan, onSelectPlan }) => {
    // const variant = item.popular ? 'default' : 'gray';
    const guideDialog = useBoolean();

    // چک می‌کنیم که آیا پلن انتخاب شده جزو این subscription هست یا نه
    const isSelectedPlanInThisSubscription = useMemo(() => {
        if (!selectedPlan) return false;
        return item.plans.some((plan) => plan.id === selectedPlan.id);
    }, [selectedPlan, item.plans]);

    const allFacilities = useMemo(() => {
        return Object.keys(item).reduce<{ title: string }[]>((acc, key) => {
            if (key.endsWith('_title') && key in item) {
                const featureKey = key.replace('_title', '') as keyof Subscription;
                const enabled = Boolean(item[featureKey]);
                if (enabled) {
                    acc.push({ title: item[key as keyof Subscription] as string });
                }
            }
            return acc;
        }, []);
    }, [item]);

    return (
        <>
            <div className={cn('shadow-card flex flex-col gap-6 overflow-hidden rounded-2xl p-4')}>
                <div className='flex flex-wrap items-center gap-1'>
                    <div className='flex flex-1 flex-col gap-2'>
                        <Image
                            alt={item.name}
                            width={40}
                            height={40}
                            src={item.image ?? '/icons/apple-touch-icon.png'}
                        />
                        <div className='flex flex-col'>
                            <div className='mb-2'>
                                <h3 className='relative top-0.5 mb-2 truncate text-2xl font-semibold'>{item.name}</h3>
                                <p className='text-sm font-normal text-gray-600'>{item.description}</p>
                            </div>
                            <div className='flex justify-end'>
                                {item.video ? <HelpButton onClick={guideDialog.onTrue} /> : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex flex-col gap-3'>
                    {allFacilities.map((facilitie, index) => (
                        <Facilities text={facilitie.title} key={`facilitie-${index}`} />
                    ))}
                </div>

                <div className='flex flex-col gap-3'>
                    {item.plans.map((plan) => (
                        <PlanItem
                            plan={plan}
                            key={`plan-${plan.id}`}
                            onSelect={onSelectPlan}
                            selected={selectedPlan?.id === plan.id}
                        />
                    ))}
                </div>

                <BuyBottomSheet
                    name={item.name}
                    plans={item.plans}
                    plan={isSelectedPlanInThisSubscription ? selectedPlan : null}
                    popular={true}
                    description={item.description}
                />
            </div>

            <Dialog open={guideDialog.value} onClose={guideDialog.onFalse}>
                <div className='absolute top-4 left-4 z-50'>
                    <CloseBtn onClick={guideDialog.onFalse} />
                </div>
                <GuideVideo
                    guide={{
                        location: '',
                        video: {
                            hls: item.video?.hls,
                            url: item.video?.url
                        }
                    }}
                />
            </Dialog>
        </>
    );
};

export default SubscriptionItem;

